import React from 'react';
import { Image } from '../Image/Image';
import styles from './DesktopMenuContentPanel.scss';
import classNames from 'classnames';
import { notifyAnalytics } from '../notify-analytics';

type PanelItem = {
  id?: string;
  name: string;
  url?: string;
  imageUrl?: string;
};

type TileVariant = 'large' | 'small';

type DesktopMenuContentPanelProps = {
  data: PanelItem[];
  onClose?: () => void;
  onTabLoop?: () => void;
  onEscape?: () => void;
  hierarchy?: string;
};

const IMAGE_SIZES = {
  large: { width: 455, height: 630 },
  small: { width: 210, height: 298 },
};

const ContentTile = ({
  item,
  variant,
  onItemClick,
}: {
  item: PanelItem;
  variant: TileVariant;
  onItemClick?: (itemName: string) => void;
}) => {
  const imageSize = IMAGE_SIZES[variant];
  const tileClassName = classNames(styles.content, {
    [styles.contentLarge]: variant === 'large',
    [styles.contentSmall]: variant === 'small',
  });

  return (
    <li className={tileClassName} key={item.name}>
      <a href={item.url} onClick={() => onItemClick?.(item.name)}>
        <div className={styles.contentImage}>
          <Image
            url={item.imageUrl}
            label={item.name}
            width={imageSize.width}
            height={imageSize.height}
          />
        </div>
        <span>{item.name}</span>
      </a>
    </li>
  );
};

const renderTiles = (
  items: PanelItem[],
  variant: TileVariant,
  onItemClick?: (itemName: string) => void
) =>
  items.map(item => (
    <ContentTile
      key={item.id || `${item.name}-${item.url}`}
      item={item}
      variant={variant}
      onItemClick={onItemClick}
    />
  ));

export const DesktopMenuContentPanel = ({
  data,
  onTabLoop,
  onEscape,
  hierarchy,
}: DesktopMenuContentPanelProps) => {
  const handleItemClick = (itemName: string) => {
    notifyAnalytics(hierarchy ? `${hierarchy}|${itemName}` : itemName);
  };
  if (data?.length < 8) {
    return null;
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onEscape?.();
    }
  };

  return (
    <div
      className={styles.contentPanel}
      onKeyDown={handleKeyDown}
      data-testid="desktop-menu-content-panel"
    >
      <ul className={styles.contentGroup}>
        {renderTiles(data.slice(0, 2), 'large', handleItemClick)}
        {renderTiles(data.slice(2, 8), 'small', handleItemClick)}
      </ul>
      <button
        type="button"
        className={styles.tabLoopSentinel}
        data-testid="desktop-content-tab-loop-sentinel"
        onFocus={onTabLoop}
        aria-label="Return to current tab"
      />
    </div>
  );
};
